// Tenant-portal session. Two kinds, both presented to the server as a Bearer
// token, and the server is the only party that verifies either:
//
//   'user'   — verifying a magic-link (POST /api/portal/tenant/verify) mints an
//              ops-style tenant_admin JWT (aud:'tenant', 8h TTL). Has an `exp`
//              we can read locally, so we drop it client-side once stale.
//   'device' — a front-desk tablet credential (`ptd_…`, tenant_devices.ts),
//              delivered once in a `?d=` link and kept here so the tablet stays
//              signed in across shifts. NOT a JWT: it has no readable expiry and
//              is revoked server-side (any 401 clears it, via the existing
//              parkify:session-expired path). That's the whole reason a tablet
//              doesn't get logged out mid-Saturday.
export interface TenantSession {
  token: string
  email: string
  tenantId: string
  exp: number // unix seconds from the JWT; 0 for a device credential
  kind?: 'user' | 'device' // absent = 'user' (sessions stored before device links existed)
  /**
   * Device sessions only — the offer this tablet was set up for, from the link's
   * `?code=`. Persisted because the credential is scrubbed out of the URL on
   * arrival: without it, every reload (or nightly restart) would drop the tablet
   * back to whichever offer happens to sort first, silently un-doing the point of
   * a per-validation link. It is only the current display code; the server
   * keeps the durable program binding on the device record.
   */
  defaultCode?: string
  /**
   * Present only on a read-only "View as tenant user" session opened by Parkify
   * staff (api tenant_users/view_as.ts). `email` above is then the TARGET, so
   * every "You"/self/welcome identity keys off them; this names the staffer.
   */
  viewAs?: ViewAsInfo
}

/**
 * A Parkify staff lens. `readOnly: true` is "View as <tenant user>" (email is
 * the target); `mode: 'full'` is "Sign in as tenant" (full write, email is the
 * staffer, no target). Both live apart from a real session, in this tab only.
 */
export interface ViewAsInfo {
  operatorEmail: string
  targetEmail?: string
  readOnly: boolean
  mode?: 'full'
}

const KEY = 'parkify_tenant_session_v1'
// The view-as lens lives in sessionStorage under its own key: it never
// overwrites a real tenant session in localStorage, it is confined to the tab
// the console opened, and closing that tab ends it.
const VIEW_AS_KEY = 'parkify_tenant_viewas_v1'

// Shape-check only — proves nothing about validity, just tells a device
// credential apart from a JWT so we don't apply JWT expiry rules to it. Mirrors
// TOKEN_RE in the Worker's tenant_devices.ts.
export const DEVICE_TOKEN_RE = /^ptd_[0-9a-f]{16}_[A-Za-z0-9_-]{20,}$/

export function isDeviceToken(token: string): boolean {
  return DEVICE_TOKEN_RE.test(token.trim())
}

function decodeExp(token: string): number {
  try {
    const body = token.split('.')[1]
    if (!body) return 0
    const json = JSON.parse(atob(body.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: unknown }
    return typeof json.exp === 'number' ? json.exp : 0
  } catch {
    return 0
  }
}

function getViewAsSession(): TenantSession | null {
  try {
    const raw = sessionStorage.getItem(VIEW_AS_KEY)
    if (!raw) return null
    const s = JSON.parse(raw) as TenantSession
    if (!s?.token || !s.tenantId || !s.viewAs?.operatorEmail || !s.exp || s.exp * 1000 <= Date.now()) {
      sessionStorage.removeItem(VIEW_AS_KEY)
      return null
    }
    return s
  } catch {
    return null
  }
}

export function getStoredSession(): TenantSession | null {
  const lens = getViewAsSession()
  if (lens) return lens
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return null
    const s = JSON.parse(raw) as TenantSession
    if (!s?.token) return null
    // A device credential has no local expiry and no tenantId to carry (the
    // tablet learns its tenant from /api/tenants/me) — only the server can
    // invalidate it, by revoking the device.
    if (s.kind === 'device') return isDeviceToken(s.token) ? s : null
    if (!s.tenantId) return null
    if (!s.exp || s.exp * 1000 <= Date.now()) {
      localStorage.removeItem(KEY)
      return null
    }
    return s
  } catch {
    return null
  }
}

export function setStoredSession(
  token: string, email: string, tenantId: string, kind: 'user' | 'device' = 'user', defaultCode?: string,
  viewAs?: ViewAsInfo,
): TenantSession {
  const session: TenantSession = {
    token, email, tenantId,
    exp: kind === 'device' ? 0 : decodeExp(token),
    kind,
    ...(defaultCode ? { defaultCode } : {}),
    ...(viewAs && kind === 'user' ? { viewAs } : {}),
  }
  if (session.viewAs) {
    try { sessionStorage.setItem(VIEW_AS_KEY, JSON.stringify(session)) } catch { /* private mode */ }
    return session
  }
  // A real sign-in in this tab ends any lens here, so the two never stack.
  try { sessionStorage.removeItem(VIEW_AS_KEY) } catch { /* ignore */ }
  try { localStorage.setItem(KEY, JSON.stringify(session)) } catch { /* private mode */ }
  return session
}

/**
 * Drop the ACTIVE session: the lens if one is open in this tab (leaving any
 * real session untouched underneath), otherwise the real one.
 */
export function clearStoredSession(): void {
  try {
    if (sessionStorage.getItem(VIEW_AS_KEY)) { sessionStorage.removeItem(VIEW_AS_KEY); return }
  } catch { /* ignore */ }
  try { localStorage.removeItem(KEY) } catch { /* ignore */ }
}
